import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import MicOffIcon from "@mui/icons-material/MicOff";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import axios from "../../utils/axiosConfig";
import standardAxios from 'axios';
import IPCapture from "./ImageProctoringCapture";
import VoiceSampleStep from "./VoiceSampleStep";
import { LoaderContained } from "../LoaderProcessing";
import video_warning_mobile from "../../images/video_mobile_warning.jpg";
import video_warning_desktop from "../../images/video_warning.png";
// import { translationMissingMessage } from "./QuestionAnswerType/translationConstants";
import { adminBackendURL } from "../../utils/constants";
import { markTestActive, wasTestActiveRecently } from "../../utils/proctoringSession";
import { enterFullscreen, fullscreenRequired } from "../../utils/fullscreen";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  paper: {
    padding: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
  },
}));

export default function Instruction(props) {
  const classes = useStyles();
  const {
    examDetail,
    setViewInstruction,
    testData,
    setTestData,
    MonitoringData,
    isAutoSpeechTest,
    isVideoInterviewTest,
    isRecordedSpeechTest,
    setUtcTestFinishTime,
    setUtcTestStartTime,
  } = props;

  const url = window.location.origin

  const [hidden, setHidden] = useState(false);
  const [videoWarningModal, setVideoWarningModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [monitorFlag, setMontorFlag] = useState(examDetail[0].monitoringflag);
  const [deviceType, setDeviceType] = useState(""); // Empty initially
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // The test needs a microphone: audio / video / speaking questions or speaker monitoring.
  const speakerMonitoring = examDetail[0].speaker_dialect === true || MonitoringData?.result?.speaker_dialect === true;
  const micNeeded = !!(isVideoInterviewTest || isAutoSpeechTest || isRecordedSpeechTest || speakerMonitoring);

  // Voice sample (speaker monitoring only): read before the test starts, skipped once one is accepted
  // (resume). components/Test/VoiceSampleStep.jsx, backend controller/userTest/voiceSample.js.
  const [voiceSample, setVoiceSample] = useState({ accepted: false, sentence: "" });
  const [voiceStepOpen, setVoiceStepOpen] = useState(false);
  useEffect(() => {
    if (!speakerMonitoring) return;
    axios
      .post("/test/voice-sample/status", { userexamid: examDetail[0].id })
      .then((resp) => setVoiceSample({ accepted: !!resp.data.accepted, sentence: resp.data.sentence || "" }))
      .catch(() => {});
  }, [speakerMonitoring, examDetail]);

  const _closeInstruction = () => {
    window.location.replace("/v2/user/dashboard");
  };

  window.onbeforeunload = function (e) {
    e.preventDefault();
  };

  // Function to get platform information
  const getPlatformInfo = async () => {
    if (navigator.userAgentData) {
      const uaData = await navigator.userAgentData.getHighEntropyValues(["platform", "platformVersion"]);
      return uaData.platform || "Unknown";
    }
    // Fallback to parsing userAgent
    const ua = navigator.userAgent;
    if (/Windows/i.test(ua)) return "Windows";
    if (/Mac OS X/i.test(ua)) return "macOS";
    if (/Android/i.test(ua)) return "Android";
    if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
    if (/Linux/i.test(ua)) return "Linux";
    return "Unknown";
  };

  // Function to detect device type
  const detectDeviceType = () => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      setDeviceType("mobile");
    } else {
      setDeviceType("desktop");
    }
    console.log("Device Type:", isMobile ? "mobile" : "desktop");
  };

  // Call the function to detect device type when component mounts
  useEffect(() => {
    detectDeviceType();
  }, []);

  React.useEffect(() => {
    const requiresMedia = micNeeded || isVideoInterviewTest || examDetail[0].monitoringflag;

    if (requiresMedia) {
      const mediaConstraints = {
        audio: micNeeded,
        video: !!(isVideoInterviewTest || examDetail[0].monitoringflag),
      };

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        getPlatformInfo().then((platform) => {
          const errorMessage = "MediaDevices API is not supported in this browser.";
          console.error(errorMessage, {
            userAgent: navigator.userAgent,
            platform,
            deviceType,
            timestamp: new Date().toISOString(),
          });

          const logData = {
            examId: examDetail[0].id,
            status: "failure",
            errorName: "NotSupportedError",
            errorMessage,
            errorStack: null,
            constraints: mediaConstraints,
            userAgent: navigator.userAgent,
            platform,
            deviceType,
            additionalLogs: {},
          };

          standardAxios
            .post(`${adminBackendURL}/api/mobile-testing/store-permission-logs`, logData)
            .then((res) => console.log("Log sent:", res.data))
            .catch((err) => console.error("Error sending log:", err));

          setVideoWarningModal(true);
        });
        return;
      }

      getPlatformInfo().then((platform) => {
        console.log("Attempting to access media devices with constraints:", {
          constraints: mediaConstraints,
          userAgent: navigator.userAgent,
          platform,
          deviceType,
          timestamp: new Date().toISOString(),
        });

        navigator.mediaDevices
          .getUserMedia(mediaConstraints)
          .then((stream) => {
            console.log("Media devices accessed successfully:", {
              tracks: stream.getTracks().map((track) => ({
                kind: track.kind,
                label: track.label,
                enabled: track.enabled,
                readyState: track.readyState,
              })),
              userAgent: navigator.userAgent,
              platform,
              deviceType,
              timestamp: new Date().toISOString(),
            });

            const logData = {
              examId: examDetail[0].id,
              status: "success",
              constraints: mediaConstraints,
              userAgent: navigator.userAgent,
              platform,
              deviceType,
              additionalLogs: {
                tracks: stream.getTracks().map((track) => ({
                  kind: track.kind,
                  label: track.label,
                  enabled: track.enabled,
                  readyState: track.readyState,
                })),
              },
            };

            standardAxios
              .post(`${adminBackendURL}/api/mobile-testing/store-permission-logs`, logData)
              .then((res) => console.log("Log sent:", res.data))
              .catch((err) => console.error("Error sending log:", err));

            const _recorder = new MediaRecorder(stream);
            stream.getTracks().forEach((track) => track.stop());
          })
          .catch((error) => {
            const errorDetails = {
              errorName: error.name,
              errorMessage: error.message,
              errorStack: error.stack,
              constraints: mediaConstraints,
              userAgent: navigator.userAgent,
              platform,
              deviceType,
              timestamp: new Date().toISOString(),
              examId: examDetail[0].id,
            };
            console.error("Error accessing media devices:", errorDetails);

            const logData = {
              examId: examDetail[0].id,
              status: "failure",
              errorName: error.name,
              errorMessage: error.message,
              errorStack: error.stack,
              constraints: mediaConstraints,
              userAgent: navigator.userAgent,
              platform,
              deviceType,
              additionalLogs: {},
            };

            standardAxios
              .post(`${adminBackendURL}/api/mobile-testing/store-permission-logs`, logData)
              .then((res) => console.log("Log sent:", res.data))
              .catch((err) => console.error("Error sending log:", err));

            let userMessage = "Please allow camera and microphone permissions to proceed.";
            if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
              userMessage = "No camera or microphone found on this device. Please ensure the devices are connected and enabled.";
            } else if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
              userMessage = `Camera or microphone access was denied. Please enable permissions in your ${deviceType === "mobile" ? "device settings (e.g., Settings > Apps > Browser > Permissions)" : "browser settings (e.g., Chrome: Settings > Privacy and Security > Site Settings)"
                }.`;
            } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
              userMessage = "Camera or microphone is in use by another application. Please close other apps and try again.";
            } else if (error.name === "OverconstrainedError" || error.name === "ConstraintNotSatisfiedError") {
              // eslint-disable-next-line unused-imports/no-unused-vars
              userMessage = "The requested media settings are not supported by your device. Please try a different device or browser.";
            }

            setVideoWarningModal(true);
          });
      });
    }
  }, [isVideoInterviewTest, micNeeded, examDetail, deviceType, setVideoWarningModal]);

  // Resuming after leaving the test, on another device or after signing out: a proctored test
  // asks for a photo again, like at the start (the start photo stays the reference; a clear
  // mismatch shows the identity notice - ImageProctoringCapture). A refresh - this browser had
  // the test open moments ago (utils/proctoringSession.js) - goes straight to Resume, on phones
  // and desktops alike. The in-test photos keep checking identity either way.
  React.useEffect(() => {
    if (examDetail[0].status === "001") {
      setMontorFlag(wasTestActiveRecently(examDetail[0].id) ? false : examDetail[0].monitoringflag);
      setHidden(false);
    }
  }, [examDetail, setMontorFlag, setHidden]);

  // Every way into the test (Start, the photo step's Proceed): the voice sample first when the test
  // has speaker monitoring and none is accepted yet; its Continue then starts the test.
  const beginTest = () => {
    if (speakerMonitoring && !voiceSample.accepted) {
      setVoiceStepOpen(true);
      return;
    }
    resumeTest();
  };

  const resumeTest = async () => {
    // Full screen is mandatory on desktops: asked for here, in the click (browsers refuse it
    // otherwise); if refused, the test page covers the questions until it is on.
    if (fullscreenRequired()) enterFullscreen();
    setLoading(true);
    if (examDetail[0].status === "000") {
      // Ensure time_remaining is always set before sending to backend
      const timeRemaining = examDetail[0].time_remaining;
      const isValidTimeRemaining = timeRemaining !== null && timeRemaining !== undefined && timeRemaining !== "" && !isNaN(Number(timeRemaining));
      if (!isValidTimeRemaining) {
        const totalTestTime = examDetail[0].total_test_time || (examDetail[0].instanceduration * 60);
        examDetail[0].time_remaining = totalTestTime;
        examDetail[0].time_left = totalTestTime;
        console.log("Investigate: time_remaining null in Instruction.jsx - START EXAM, set to full time:", totalTestTime);
      }

      setTestData({ quesData: testData, examDetail: examDetail });
      let stringifyTestData = JSON.stringify(testData);
      let encodedTestData = encodeURIComponent(btoa(encodeURIComponent(stringifyTestData)));
      let stringifyExamDetail = JSON.stringify(examDetail);
      let encodedExamDetail = encodeURIComponent(btoa(encodeURIComponent(stringifyExamDetail)));

      axios
        .post("/test/startExam", {
          headers: {
            "Access-Control-Allow-Origin": "*",
          },
          id: examDetail[0].id,
          selectedLanguage: examDetail[0].selectedLanguage,
          quesData: encodedTestData,
          examDetail: encodedExamDetail,
        })
        .then((updateStatus) => {
          examDetail[0].status = "001";
          markTestActive(examDetail[0].id);
          console.log("start exam end time:", updateStatus.data.exam_end_time);
          setUtcTestStartTime(updateStatus.data.exam_start_time);
          setUtcTestFinishTime(updateStatus.data.exam_end_time);

          setViewInstruction(false);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed to start exam:", error);
          // Ensure local storage is cleared if backend retrieval fails
          window.localStorage.removeItem(examDetail[0].id);

          setLoading(false);
          setErrorMessage("Failed to start the test. Please check your internet connection and try again.");
          setShowErrorModal(true);
        });
    } else if (examDetail[0].status === "001") {
      // Resume always uses the LIVE testData/examDetail props - never a cached
      // localStorage snapshot. Those props are already the best data this page has:
      // either freshly fetched via /test/answeredQts or /test/userTest (the normal
      // Dashboard-resume path, modalHandler.js), or - on a genuine hard page reload -
      // Test.jsx's own localStorage fallback (which reads this exact same cached key
      // once, at that one point, then hands it down as these same props).
      //
      // Previously this branch read window.localStorage.getItem(examDetail[0].id)
      // AGAIN here and PREFERRED it over the props whenever any cached entry existed
      // for this exam id - even on a completely normal in-app resume where the props
      // were already correct. That cached entry is written ONCE, by modalHandler.js,
      // the very first time the candidate opens the exam from the Dashboard - nothing
      // during live answering ever refreshes it. Confirmed live in a real incident
      // (userexamsid 892210): the candidate's session spanned 11+ hours with multiple
      // reloads; a later reload fed this stale, pre-answering blank snapshot back into
      // /test/resumeExam, which overwrote the candidate's real 16/16 answered progress
      // in Mongo with it - "responses deleted, moved back to question 1," and the
      // exam eventually had to be manually reassigned. Backend now also independently
      // guards against this (see mergeQuesData in updateTestAnsweredQts) - removing
      // this read closes the vector at its source instead of just containing it.
      const quesDataToUse = testData;
      const examDetailToUse = examDetail;

      // Ensure time_remaining is always set before sending to backend (resume).
      // Never fabricate a small number here - a `1` reads as "already expired" to the
      // backend's own validity check, turning a missing local value into an instant false
      // timeout. Fall back to the full allotted duration, same as the start branch above.
      const timeRemainingResume = examDetailToUse[0].time_remaining;
      const isValidTimeRemainingResume = timeRemainingResume !== null && timeRemainingResume !== undefined && timeRemainingResume !== "" && !isNaN(Number(timeRemainingResume));
      if (!isValidTimeRemainingResume) {
        const totalTestTimeResume = examDetailToUse[0].total_test_time || (examDetailToUse[0].instanceduration * 60);
        examDetailToUse[0].time_remaining = totalTestTimeResume;
        examDetailToUse[0].time_left = totalTestTimeResume;
        console.log("Investigate: time_remaining null in Instruction.jsx - RESUME EXAM, falling back to full allotted duration:", totalTestTimeResume);
      }

      setTestData({
        quesData: quesDataToUse,
        examDetail: examDetailToUse,
      });

      const stringifyExamDetail = JSON.stringify(examDetailToUse[0]);
      const encodedExamDetailFinal = encodeURIComponent(btoa(encodeURIComponent(stringifyExamDetail)));

      const stringifyQuesData = JSON.stringify(quesDataToUse);
      const encodedQuesDataFinal = encodeURIComponent(btoa(encodeURIComponent(stringifyQuesData)));

      axios
        .post("/test/resumeExam", {
          headers: {
            "Access-Control-Allow-Origin": "*",
          },
          id: examDetail[0].id,
          examDetail: encodedExamDetailFinal,
          quesData: encodedQuesDataFinal,
        })
        .then((updateStatus) => {
          console.log("resume resp:", updateStatus.data.exam_end_time);

          // Check if test has timed out
          if (updateStatus.data.msg === "TIMEOUT") {
            setLoading(false);
            setShowTimeoutModal(true);
            return;
          }

          setUtcTestStartTime(updateStatus.data.exam_start_time);
          setUtcTestFinishTime(updateStatus.data.exam_end_time);

          examDetail[0].status = "001";
          markTestActive(examDetail[0].id);

          setViewInstruction(false);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed to resume exam:", error);
          // Ensure local storage is cleared if backend retrieval fails
          window.localStorage.removeItem(examDetail[0].id);

          setLoading(false);
          setErrorMessage("Failed to resume the test. Please check your internet connection and try again.");
          setShowErrorModal(true);
        });
    } else {
      setViewInstruction(false);
    }
  };

  const replaceInstructionVars = (text) => {
    if (!text) return text;
    return text
      .replace(/\$noofqts\$/g, String(examDetail[0].instanceqcount || ''))
      .replace(/\$testtime\$/g, String(examDetail[0].instanceduration || ''));
  };

  return (
    <>
      <div id="main-body" style={{ background: "#E5E6E9", maxHeight: "-webkit-fill-available" }}>
        <div className="container-fluid main-wrap">
          <AppBar position="static" className="col-md-12" color="secondary">
            <Toolbar style={{ minHeight: "32px" }}>
              <Typography variant="h6" className={classes.title}>
                {examDetail[0].instancename}
              </Typography>
            </Toolbar>
          </AppBar>
        </div>
        <div className="row">
          <div className="container container-two qa-wrap">
            <div className="qa-overlay" id="main-layout"></div>
            <div className="col-md-12 question-box instruction-li" id="layout-2">
              {(examDetail[0].selectedLanguage === null || examDetail[0].selectedLanguage === "en-IN" || examDetail[0].include_english === true) || (examDetail[0].selectedLanguage !== null && examDetail[0].selectedLanguage !== "en-IN" && examDetail[0].translated_instructions === null) ? <div id="inst" dangerouslySetInnerHTML={{ __html: replaceInstructionVars(examDetail[0].instruction_text) }} /> : null}
              {examDetail[0].selectedLanguage !== null && examDetail[0].selectedLanguage !== "en-IN" && (examDetail[0].include_english === true || examDetail[0].translated_instructions === null) ? <><br /><br /></> : null}
              {examDetail[0].selectedLanguage !== null && examDetail[0].selectedLanguage !== "en-IN" ? <div id="inst" dangerouslySetInnerHTML={{ __html: replaceInstructionVars(examDetail[0].translated_instructions) || "" }} /> : null}
            </div>
          </div>
        </div>
        <div className="instruction-div container qa-wrap ">
          <div className="qa-overlay" id="main-layout"></div>
          <div className="col-md-12 question-box" style={{ paddingTop: "0px", paddingBottom: "0px" }} id="layout-2">
            {examDetail[0].test_type !== "7" && examDetail[0].test_type !== "8" ? (
              <>
                <h3 style={{ textAlign: "center", color: "#8D0001", marginTop: "10px", marginBottom: "10px" }}>
                  <b>Important Information</b>
                </h3>
                <p style={{ marginBottom: "0px" }}>
                  Your test will be{" "}
                  <u>
                    <b style={{ color: "#8D0001" }}>TERMINATED</b>
                  </u>{" "}
                  &amp; considered as complete if you attempt to switch to another window.
                </p>
                <p style={{ marginBottom: "0px" }}>Use of computer calculator will also be considered as switching window.</p>
                <p style={{ marginBottom: "0px" }}>
                  Please note : Your Test Score will be considered as{" "}
                  <u>
                    <b style={{ color: "#8D0001" }}>INVALID</b>.
                  </u>
                </p>
              </>
            ) : (
              <div style={{ margin: "10px 0" }}>
                <p style={{ marginBottom: "0px" }}>Answer all questions honestly. There are no right or wrong answers.</p>
                <p style={{ marginBottom: "0px" }}>Click the <b>Start</b> button below when you are ready to begin.</p>
              </div>
            )}
            {!hidden && monitorFlag ? (
              <></>
            ) : hidden ? (
              <></>
            ) : (
              <Grid item xs={12} style={{ textAlign: "center", paddingTop: "16px", paddingBottom: "16px" }}>
                {loading ? (
                  <LoaderContained />
                ) : (
                  <Button variant="contained" color="primary" onClick={() => beginTest()}>
                    <strong>{examDetail[0].status === "000" ? "Start" : "Resume"}</strong>
                  </Button>
                )}
              </Grid>
            )}
          </div>
        </div>
        {monitorFlag !== false ? (
          <div className="instruction-div-two container  qa-wrap ">
            <div className="qa-overlay" id="main-layout"></div>
            <div className="col-md-12  question-box" style={{ paddingTop: "0px", paddingBottom: "0px" }} id="layout-2">
              <h3 style={{ textAlign: "center", color: "#8D0001", marginTop: "10px", marginBottom: "10px" }}>
                <b>Web Cam Configurations</b>
              </h3>
              <p style={{ marginBottom: "0px" }}>
                This test needs to be conducted under monitored session only. Webcam is{" "}
                <u>
                  <b style={{ color: "#8D0001" }}>MANDATORY</b>
                </u>
                . This test will not start if webcam is not found.
              </p>
              <p style={{ marginBottom: "0px" }}>
                Basic System Requirements : Windows OS 7 and above, iOS High Sierra and above, Chrome 55 and above, Firefox 50 and above
              </p>
              <p style={{ marginBottom: "0px" }}>
                {" "}
                Click on the Start button. A small window might appear on the top of the page, asking you to allow access to your camera. Allow it.
              </p>
              <p style={{ marginBottom: "0px" }}>
                To snap photo. Please come closer to the camera so that your face is visible.
              </p>

              <Grid item xs={12}>
                <IPCapture resumeTest={beginTest} micNeeded={micNeeded} examDetail={examDetail} hidden={hidden} setHidden={setHidden} MonitoringData={MonitoringData} />
              </Grid>
            </div>
          </div>
        ) : (
          <> </>
        )}
      </div>

      {videoWarningModal && (
        <Dialog open={videoWarningModal} onClose={() => setVideoWarningModal(false)}>
          <DialogTitle style={{ color: "#FF0000" }}>
            <div style={{ display: "flex", alignItems: "center", textAlign: "left" }}>
              <MicOffIcon style={{ fontSize: "2em", marginRight: "8px", color: "#000" }} />
              <b style={{ marginLeft: "8px" }}>
                You need to allow{isVideoInterviewTest || examDetail[0].monitoringflag ? " camera " : ""}
                {(isVideoInterviewTest || examDetail[0].monitoringflag) && micNeeded ? "and" : ""}
                {micNeeded ? " mic " : ""}permissions to
                proceed further.
              </b>
            </div>
          </DialogTitle>
          <DialogContent>
            <div style={{ textAlign: "center" }}>
              <img
                src={deviceType === "mobile" ? video_warning_mobile : video_warning_desktop}
                alt="Video Warning"
                style={{ maxWidth: "100%", maxHeight: "400px" }}
              />
            </div>
          </DialogContent>
          <DialogActions style={{ justifyContent: "center" }}>
            <Button onClick={() => setVideoWarningModal(false)} color="primary" variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Timeout Modal */}
      <Dialog
        open={showTimeoutModal}
        onClose={() => { }}
        disableEscapeKeyDown
        PaperProps={{
          style: {
            borderRadius: "15px",
            padding: "10px",
            minWidth: "380px",
          },
        }}
      >
        <DialogTitle style={{ textAlign: "center", color: "#6f2981" }}>
          <b>System Warning</b>
        </DialogTitle>
        <DialogContent style={{ textAlign: "justify" }}>
          <p>
            Your {examDetail[0].test_type === "7" || examDetail[0].test_type === "8" ? "Survey" : "Test"} time has expired and has been auto-submitted.
          </p>
          <p>
            Thanking you,
            <br />
            Support, AssessHub.
          </p>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Button
            onClick={() => {
              window.onbeforeunload = function () {
                return null;
              };
              window.location.href = `${url}/v2/user/dashboard`;
            }}
            color="primary"
            variant="contained"
            style={{ borderRadius: "10px" }}
          >
            <strong>Close</strong>
          </Button>
        </DialogActions>
      </Dialog>
      {/* Error Modal */}
      <Dialog
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        PaperProps={{
          style: {
            borderRadius: "15px",
            padding: "15px", // Slightly more padding for better look
            width: "380px", // Standard size across the platform
            maxWidth: "90%", // Responsive fallback
          },
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h3 id="transition-modal-title" style={{ color: "#6f2981", marginBottom: "30px" }}>
            System Warning
          </h3>
        </div>
        <DialogContent style={{ textAlign: "justify", padding: "0 10px" }}>
          <p>{errorMessage}</p>
        </DialogContent>
        <DialogActions style={{ justifyContent: "center", paddingBottom: "15px" }}>
          <Button
            onClick={() => setShowErrorModal(false)}
            color="primary"
            variant="contained"
            style={{ borderRadius: "10px" }}
          >
            <strong>Close</strong>
          </Button>
        </DialogActions>
      </Dialog>
      <VoiceSampleStep
        open={voiceStepOpen}
        userexamId={examDetail[0].id}
        sentence={voiceSample.sentence}
        onDone={() => {
          setVoiceSample((v) => ({ ...v, accepted: true }));
          setVoiceStepOpen(false);
          resumeTest();
        }}
      />
    </>
  );
}
